// Browser SpeechRecognition wrapper. Free, runs locally (no API), works in
// Chromium/Edge and recent Firefox. Honors the user's language preference.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Language } from './prefs.js';
import { loadTts, resolveVoice, synthViaServer, reportTtsFallback, effectiveEngine } from './tts.js';
import { playSpeechUrl, stopSpeechUrl, startSyntheticSpeech, stopSyntheticSpeech } from './audioBus.js';

type SRConstructor = new () => SpeechRecognition;

interface SREventResult { transcript: string; confidence: number }
interface SREventAlternative { 0: SREventResult; length: number; isFinal: boolean }
interface SREvent extends Event {
  results: ArrayLike<SREventAlternative>;
  resultIndex: number;
}
interface SpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SREvent) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: (() => void) | null;
}

function getSRConstructor(): SRConstructor | null {
  const w = window as unknown as { SpeechRecognition?: SRConstructor; webkitSpeechRecognition?: SRConstructor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const BCP47: Record<Language, string> = { fr: 'fr-CA', en: 'en-US', es: 'es-ES' };

// ── Text-to-speech (browser SpeechSynthesis) ───────────────────────────────
// Free, local, no install. Picks a voice matching the requested language.

let voicesCache: SpeechSynthesisVoice[] = [];

function ensureVoices(): SpeechSynthesisVoice[] {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];
  if (voicesCache.length > 0) return voicesCache;
  voicesCache = window.speechSynthesis.getVoices();
  return voicesCache;
}
if (typeof window !== 'undefined' && window.speechSynthesis) {
  window.speechSynthesis.onvoiceschanged = () => { voicesCache = window.speechSynthesis.getVoices(); };
}

function pickVoice(lang: Language): SpeechSynthesisVoice | undefined {
  const want = BCP47[lang];
  const vs = ensureVoices();
  return vs.find((v) => v.lang === want)
      ?? vs.find((v) => v.lang.startsWith(lang))
      ?? vs.find((v) => v.default);
}

export type VoiceEngine = 'elevenlabs' | 'system';
export interface VoiceStatus { engine: VoiceEngine; fallback: boolean; reason?: string }

export function useSpeaker() {
  const [speaking, setSpeaking] = useState(false);
  // Which voice actually produced the last/current audio, and whether it was a
  // fallback to the system voice (with the reason).
  const [voice, setVoice] = useState<VoiceStatus | null>(null);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const supported = typeof window !== 'undefined' && !!window.speechSynthesis;

  const cleanupAudio = useCallback(() => {
    stopSpeechUrl();
    audioRef.current = null;
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
  }, []);

  const stop = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
    cleanupAudio();
    setSpeaking(false);
  }, [cleanupAudio]);

  // System voice (browser SpeechSynthesis) — the free, local path + fallback.
  // `fb` carries the fallback reason when we got here after ElevenLabs failed.
  const speakSystem = useCallback((text: string, language: Language, fb?: { reason: string; detail?: string; report?: boolean }) => {
    setVoice({ engine: 'system', fallback: Boolean(fb), reason: fb?.reason });
    if (fb?.report) reportTtsFallback(fb.reason, fb.detail);
    if (!supported) return;
    window.speechSynthesis.cancel();
    const tts = loadTts();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = BCP47[language];
    const v = resolveVoice(tts, language) ?? pickVoice(language);
    if (v) u.voice = v;
    u.rate = Number.isFinite(tts.rate) ? Math.min(2, Math.max(0.5, tts.rate)) : 1;
    u.pitch = Number.isFinite(tts.pitch) ? Math.min(2, Math.max(0, tts.pitch)) : 1;
    u.volume = 1;
    // System voice exposes no audio data → drive a synthetic mouth envelope so
    // the avatar still lip-syncs believably while it talks.
    u.onstart = () => { setSpeaking(true); startSyntheticSpeech(); };
    u.onend = () => { setSpeaking(false); stopSyntheticSpeech(); };
    u.onerror = () => { setSpeaking(false); stopSyntheticSpeech(); };
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, [supported]);

  const speak = useCallback((text: string, language: Language) => {
    if (!text.trim()) return;
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
    cleanupAudio();

    const tts = loadTts();
    if (effectiveEngine(tts) === 'elevenlabs') {
      // Cloud voice: fetch synthesized audio and play it. We retry once on a
      // TRANSIENT failure (network blip, rate-limit, 5xx) before falling back to
      // the system voice — this avoids the voice flapping mid-conversation. A
      // permanent 4xx (e.g. 400 voice_not_fine_tuned, 401, 413 too_long) won't
      // change on a retry, so we fall back immediately instead of doubling the
      // latency and the fallback-log noise.
      setSpeaking(true);
      const retryable = (o: Awaited<ReturnType<typeof synthViaServer>>) =>
        !o.ok && (o.kind === 'network' || (o.kind === 'http' && (o.status === 429 || o.status >= 500)));
      const fetchWithRetry = async () => {
        const first = await synthViaServer(text, language);
        if (first.ok || !retryable(first)) return first;
        await new Promise((r) => setTimeout(r, 400));
        return synthViaServer(text, language);
      };
      fetchWithRetry().then(async (out) => {
        if (!out.ok) {
          // http failures are already logged server-side; network ones are not.
          const reason = out.kind === 'network' ? 'network' : (out.error ?? `http_${out.status}`);
          setSpeaking(false);
          speakSystem(text, language, { reason, detail: out.detail, report: out.kind === 'network' });
          return;
        }
        const url = URL.createObjectURL(out.blob);
        urlRef.current = url;
        // Reuse the persistent, gesture-unlocked element so delayed playback (the
        // synth often takes several seconds) isn't blocked by the autoplay policy.
        const el = await playSpeechUrl(url, {
          onPlay: () => { setVoice({ engine: 'elevenlabs', fallback: false }); setSpeaking(true); },
          onEnd: () => { cleanupAudio(); setSpeaking(false); },
        });
        if (!el) {
          cleanupAudio(); setSpeaking(false);
          speakSystem(text, language, { reason: 'autoplay_blocked', report: true });
          return;
        }
        audioRef.current = el;
      }).catch(() => { setSpeaking(false); speakSystem(text, language, { reason: 'client_error', report: true }); });
      return;
    }

    // User explicitly chose the system voice — not a fallback.
    speakSystem(text, language);
  }, [cleanupAudio, speakSystem]);

  useEffect(() => () => { stop(); }, [stop]);

  return { supported, speaking, speak, stop, voice };
}

export interface UseSpeechReturn {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
}

/**
 * Hook to capture speech. Calls `onText` with each final segment (so the caller
 * can append to whatever input it owns). The transcript is NOT stored here —
 * the composer is the source of truth.
 */
export function useSpeechRecognition(language: Language, onText: (text: string) => void): UseSpeechReturn {
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognition | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const supported = typeof window !== 'undefined' && getSRConstructor() != null;

  useEffect(() => {
    return () => { recRef.current?.abort(); recRef.current = null; };
  }, []);

  const start = useCallback(() => {
    const Ctor = getSRConstructor();
    if (!Ctor) return;
    const rec: SpeechRecognition = new Ctor();
    rec.lang = BCP47[language];
    rec.continuous = true;
    rec.interimResults = false; // only final segments are appended
    rec.onresult = (e: SREvent) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r) continue;
        if (r.isFinal) {
          const t = r[0]?.transcript?.trim();
          if (t) onTextRef.current(t);
        }
      }
    };
    rec.onerror = () => { setListening(false); };
    rec.onend = () => { setListening(false); recRef.current = null; };
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  }, [language]);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setListening(false);
  }, []);

  return { supported, listening, start, stop };
}
